import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal",
    "stepLocation", "stepPincode", "stepLoading", "stepSuccess", "stepError",
    "pincodeInput", "successMessage", "errorMessage", "validationError",
    "locationBtn", "pincodeBtn", "geoError", "geoBlocked"
  ]

  static values = {
    coordinatesUrl: String,
    pincodeUrl:     String,
    cartPath:       String
  }

  connect() {
    this._pendingCart = null
    this._step = "location"
    this._cachedCoords = null

    // Sync localStorage cache with server-side address check on every page load.
    // "deliverable"  → saved address is within range: auto-approve
    // "out-of-range" → saved address exists but is too far: clear cache so popup shows
    // "no-address"   → no saved address: leave cache alone so pincode verification persists
    const precheck = document.querySelector('meta[name="delivery-precheck"]')
    if (precheck?.content === "deliverable") {
      if (!this._getCache()) this._setCache({ deliverable: true })
    } else if (precheck?.content === "out-of-range") {
      this._clearCache()
    }

    this._onKeydown = (e) => {
      if (this.modalTarget.classList.contains("hidden")) return

      if (this._step === "location") {
        if (e.key === "Enter")     { e.preventDefault(); this.requestLocation() }
        if (e.key === "ArrowDown") { e.preventDefault(); this.showPincodeStep() }
      } else if (this._step === "pincode") {
        if (e.key === "Enter")   { e.preventDefault(); this.checkByPincode() }
        if (e.key === "ArrowUp") { e.preventDefault(); this.showLocationStep() }
      }
    }
    document.addEventListener("keydown", this._onKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
  }

  get cacheKey() { return "fo_delivery_check" }
  get cacheTtl()  { return 24 * 60 * 60 * 1000 }

  // ── Cart icon intercept ─────────────────────────────────────────────────────

  open(event) {
    event.preventDefault()

    const cached = this._getCache()
    if (cached?.deliverable) {
      window.location.href = this.cartPathValue
      return
    }

    this._pendingCart = null
    this._showModal()

    if (cached && cached.deliverable === false) {
      this.errorMessageTarget.textContent =
        "We don't deliver to your saved location. Try a different one?"
      this._showStep("error")
    } else {
      this._showStep("location")
    }
  }

  interceptAddToCart(event) {
    const cached = this._getCache()
    if (cached?.deliverable) return

    event.preventDefault()

    const form = event.currentTarget.closest("form")
    this._pendingCart = {
      url:  form.action,
      body: new FormData(form)
    }

    this._showModal()
    if (cached && cached.deliverable === false) {
      this.errorMessageTarget.textContent =
        "We don't deliver to your location. Try a different one?"
      this._showStep("error")
    } else {
      this._showStep("location")
    }
  }

  // ── Modal actions ───────────────────────────────────────────────────────────

  closeModal() {
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    this._pendingCart = null
  }

  async requestLocation() {
    if (this._cachedCoords) {
      this._showStep("loading")
      this._checkByCoords(this._cachedCoords.lat, this._cachedCoords.lng)
      return
    }
    if (!navigator.geolocation) {
      this.geoBlockedTarget.classList.remove("hidden")
      return
    }

    // Check permission state before prompting so we can show the right message
    // immediately instead of waiting for getCurrentPosition to fail.
    if (navigator.permissions) {
      const status = await navigator.permissions.query({ name: "geolocation" })
      if (status.state === "denied") {
        this.geoBlockedTarget.classList.remove("hidden")
        return
      }
    }

    this._showStep("loading")
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this._cachedCoords = { lat: pos.coords.latitude, lng: pos.coords.longitude }
        this._checkByCoords(pos.coords.latitude, pos.coords.longitude)
      },
      (err) => {
        if (err.code === 1) {
          this._showStep("location")
          this.geoBlockedTarget.classList.remove("hidden")
        } else {
          this._showStep("location")
          this.geoErrorTarget.classList.remove("hidden")
        }
      },
      { timeout: 10000, maximumAge: 300000 }
    )
  }

  showPincodeStep() {
    this._hideValidationError()
    this._showStep("pincode")
  }

  showLocationStep() { this._showStep("location") }

  async checkByPincode() {
    const pincode = this.pincodeInputTarget.value.trim()
    if (!/^\d{6}$/.test(pincode)) {
      this._showValidationError("Please enter a valid 6-digit pincode")
      return
    }
    this._hideValidationError()
    this._showStep("loading")

    const data = await this._post(this.pincodeUrlValue, { pincode })
    if (!data) {
      this._showStep("pincode")
      this._showValidationError("Something went wrong. Please try again.")
      return
    }
    if (data.error) {
      this._showStep("pincode")
      this._showValidationError(data.error)
      return
    }
    this._handleResult(data)
  }

  proceed() {
    window.location.href = this.cartPathValue
  }

  tryAnother() {
    this._clearCache()
    this._pendingCart = null
    this._showStep("location")
  }

  // ── Internal ────────────────────────────────────────────────────────────────

  async _checkByCoords(lat, lng) {
    const data = await this._post(this.coordinatesUrlValue, { latitude: lat, longitude: lng })
    if (!data) { this._showStep("location"); return }
    this._handleResult(data)
  }

  _handleResult(data) {
    if (data.deliverable) {
      this._setCache({ deliverable: true })

      if (this._pendingCart) {
        const cart = this._pendingCart
        this._pendingCart = null
        this.closeModal()
        this._submitCart(cart)
        return
      }

      const dist = data.distance_km ? ` (${data.distance_km} km from our store)` : ""
      const area = data.area        ? ` to ${data.area}` : ""
      this.successMessageTarget.textContent = `We deliver${area}!${dist}`
      this._showStep("success")
    } else {
      this._setCache({ deliverable: false })
      this._pendingCart = null
      const dist = data.distance_km ? ` (${data.distance_km} km away)` : ""
      this.errorMessageTarget.textContent =
        `Sorry, we don't deliver to your area yet.${dist}`
      this._showStep("error")
    }
  }

  _submitCart({ url, body }) {
    const form = document.createElement("form")
    form.method = "POST"
    form.action = url
    form.style.display = "none"

    for (const [key, value] of body.entries()) {
      const input = document.createElement("input")
      input.type  = "hidden"
      input.name  = key
      input.value = value
      form.appendChild(input)
    }

    document.body.appendChild(form)
    form.requestSubmit()
    requestAnimationFrame(() => form.remove())
  }

  async _post(url, body) {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content ?? ""
        },
        body: JSON.stringify(body)
      })
      return await res.json()
    } catch {
      return null
    }
  }

  _showModal() {
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  _showStep(name) {
    this._step = name
    const map = {
      location: this.stepLocationTarget,
      pincode:  this.stepPincodeTarget,
      loading:  this.stepLoadingTarget,
      success:  this.stepSuccessTarget,
      error:    this.stepErrorTarget
    }
    Object.values(map).forEach(el => el.classList.add("hidden"))
    map[name]?.classList.remove("hidden")

    if (name === "location") {
      this.geoErrorTarget.classList.add("hidden")
      this.geoBlockedTarget.classList.add("hidden")
    }
    if (name === "pincode") this.pincodeInputTarget.focus()
  }

  _showValidationError(msg) {
    this.validationErrorTarget.textContent = msg
    this.validationErrorTarget.classList.remove("hidden")
  }

  _hideValidationError() {
    this.validationErrorTarget.classList.add("hidden")
  }

  _getCache() {
    try {
      const raw = localStorage.getItem(this.cacheKey)
      if (!raw) return null
      const obj = JSON.parse(raw)
      if (Date.now() > obj.expires) { localStorage.removeItem(this.cacheKey); return null }
      return obj
    } catch { return null }
  }

  _setCache(data) {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify({
        ...data,
        expires: Date.now() + this.cacheTtl
      }))
    } catch {}
  }

  _clearCache() {
    localStorage.removeItem(this.cacheKey)
  }
}
